import { Controller } from "@hotwired/stimulus"

const OFF = "0"

/**
 * Remembered URL Toggle Controller
 *
 * A checkbox toggle that filters a page through a query parameter and remembers
 * where each user left it (TMS-1780, the Companies index Pilot toggle).
 *
 * Clicking the toggle saves its state in localStorage and navigates with the
 * parameter set either way, so the URL always shows the state and a shared link
 * opens the same view. Opening the page with no parameter applies a saved "on"
 * by replacing the URL. A parameter already in the URL wins and is never saved:
 * following a colleague's link must not change the reader's own default.
 *
 * Usage:
 *   <input type="checkbox" name="pilot" value="1"
 *     data-controller="remembered-url-toggle"
 *     data-remembered-url-toggle-url-value="/admin/companies"
 *     data-remembered-url-toggle-storage-key-value="admin-companies-pilot:42"
 *     data-action="change->remembered-url-toggle#toggle" />
 *
 * Values:
 *   - url: the page URL to navigate to, without this parameter or the page number.
 *   - storageKey: the localStorage key; include the user id so people who share a
 *     browser keep their own setting.
 *   - restore: false on a page rendered from a form submission (a failed create
 *     re-rendering the index with its errors) — replacing that URL would throw the
 *     errors away before anyone reads them.
 */
export default class extends Controller {
  static values = { url: String, storageKey: String, restore: { type: Boolean, default: true } }

  connect() {
    if (!this.restoreValue) return
    if (this.parameterInCurrentUrl()) return
    if (this.readSavedState() !== this.element.value) return

    window.location.replace(this.urlWith(this.element.value))
  }

  toggle() {
    const state = this.element.checked ? this.element.value : OFF

    this.saveState(state)
    window.location.href = this.urlWith(state)
  }

  parameterInCurrentUrl() {
    return new URL(window.location.href).searchParams.has(this.element.name)
  }

  urlWith(state) {
    const url = new URL(this.urlValue || window.location.href, window.location.origin)
    url.searchParams.set(this.element.name, state)
    return url.toString()
  }

  // Storage can throw (private mode, blocked site data); the toggle then works as
  // a plain URL filter that starts off.
  readSavedState() {
    try {
      return window.localStorage.getItem(this.storageKeyValue)
    } catch {
      return null
    }
  }

  saveState(state) {
    try {
      window.localStorage.setItem(this.storageKeyValue, state)
    } catch {
      // Nothing to remember it in; the navigation below still applies the filter.
    }
  }
}
